import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  SelectChangeEvent,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { partnerService, CreatePartnerDto } from "../../apis/partners";
import { offerTypeService, OfferType } from "../../apis/offerTypes";

interface CreatePartnerModalProps {
  open: boolean;
  onClose: () => void;
}

const countries: Record<string, string> = {
  uk: "UK",
  france: "France",
  "ivory-coast": "Ivory Coast",
};

const CreatePartnerModal: React.FC<CreatePartnerModalProps> = ({ open, onClose }) => {
  const [formData, setFormData] = useState<CreatePartnerDto>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phone: "",
    offerTypeId: "",
    countryCode: "uk",
    paymentFrequency: 2, // Default 2 weeks
    registrationNumber: "",
    companyName: "",
  });
  const [offerTypes, setOfferTypes] = useState<OfferType[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingOfferTypes, setLoadingOfferTypes] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (open) {
      fetchOfferTypes();
    }
  }, [open]);

  const fetchOfferTypes = async () => {
    setLoadingOfferTypes(true);
    try {
      const response = await offerTypeService.getActiveOfferTypes();
      if (response.status === "success" || response.status === true) {
        setOfferTypes(response.data.offer_types || []);
        if (response.data.offer_types && response.data.offer_types.length > 0 && !formData.offerTypeId) {
          setFormData((prev) => ({ ...prev, offerTypeId: response.data.offer_types[0]._id }));
        }
      }
    } catch (error) {
      console.error("Failed to fetch offer types:", error);
    } finally {
      setLoadingOfferTypes(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name as string]: value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name as string]: value,
    }));
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.phone || !formData.offerTypeId) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      await partnerService.createPartner(formData);
      setSnackbar({
        open: true,
        message: "Partner created successfully. Login credentials have been sent via email.",
        severity: "success",
      });
      setTimeout(() => {
        onClose();
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          phone: "",
          offerTypeId: offerTypes.length > 0 ? offerTypes[0]._id : "",
          countryCode: "uk",
          paymentFrequency: 2,
          registrationNumber: "",
          companyName: "",
        });
      }, 1500);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to create partner",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Partner</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="Registration Number"
              name="registrationNumber"
              value={formData.registrationNumber}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Company Name"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl fullWidth required>
              <InputLabel>Offer Type</InputLabel>
              <Select
                name="offerTypeId"
                value={formData.offerTypeId}
                onChange={handleSelectChange}
                label="Offer Type"
                disabled={loadingOfferTypes}
              >
                {offerTypes.map((offerType) => (
                  <MenuItem key={offerType._id} value={offerType._id}>
                    {offerType.name} ({offerType.partner_percentage}% of Teracar Commission)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Country</InputLabel>
              <Select
                name="countryCode"
                value={formData.countryCode}
                onChange={handleSelectChange}
                label="Country"
              >
                {Object.entries(countries).map(([code, name]) => (
                  <MenuItem key={code} value={code}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Payment Frequency</InputLabel>
              <Select
                name="paymentFrequency"
                value={formData.paymentFrequency?.toString() || "2"}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  setFormData((prev) => ({ ...prev, paymentFrequency: value }));
                }}
                label="Payment Frequency"
              >
                <MenuItem value="1">1 Week</MenuItem>
                <MenuItem value="2">2 Weeks</MenuItem>
                <MenuItem value="3">3 Weeks</MenuItem>
                <MenuItem value="4">4 Weeks</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? "Creating..." : "Create Partner"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default CreatePartnerModal;

